import React, { useState, useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import { api as learningPath } from "../../../../../api/learningPath";
import EmptyPlaceHolder from "../../../../../components/EmptyPlaceHolder";
import Pagination from "../../../../../components/pagination/Pagination";
import Table from "../../../../../components/table/Table";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import LearnerSummary from "./components/LearnerSummary";
import toast from "react-hot-toast";
import FileSaver from "file-saver";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import ResetPassword from "./components/ResetPassword";
import CenteredModal from "../../../../../components/Modal/CenteredModal";

interface ViewUsersProps {
  activeCohortId: string | null;
}

interface User {
  participant: {
    createdAt: string;
    user: {
      name: string;
      email: string;
      id: string;
    };
  };
  cohort: {
    status: string;
    enrolledAt: string;
    createdAt: string;
    updatedAt: string;
  };
}

interface Meta {
  page: number;
  perPage: number;
  total: number;
}

interface UsersResponse {
  data: User[];
  meta: Meta;
}

const ViewLearners: React.FC = () => {
  const { id } = useParams();
  const activeCohortId = id;
  const [perPage, setPerPage] = useState<number>(5);
  const [search, setSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filter, setFilter] = useState<string>("");
  const [openResetModal, setOpenResetModal] = useState<boolean>(false);
  const currentLearner = JSON.parse(sessionStorage.getItem("currentLearner")!);

  const filterStatus = [
    { title: "Completed", value: "completed" },
    { title: "In Progress", value: "in progress" },
    { title: "Not Started", value: "not started" },
  ];

  const orgId = JSON.parse(sessionStorage.getItem("user") || "{}")?.org?.id;
  const { mutateAsync, isLoading: isDownloading } = useMutation(
    learningPath.downloadCohortUsers
  );

  const {
    data: users,
    isLoading,
    refetch,
  } = useQuery(
    [
      "cohort users",
      orgId,
      activeCohortId,
      activeCohortId,
      search,
      perPage,
      currentPage,
      filter,
    ],
    () => {
      return activeCohortId
        ? learningPath.getCohortUsers({
            id: orgId,
            cohortId: activeCohortId,
            search,
            perPage,
            filter,
            currentPage,
          } as any)
        : null;
    },
    {
      staleTime: 5 * 60 * 1000, // data is considered fresh for 5 minutes
      cacheTime: 30 * 60 * 1000,
      select: (data) => data,
    }
  );

  const columns = useMemo(
    () => [
      {
        title: "ID",
        selector: null,
        key: "id",
        render: ({ index }: { index: number }) =>
          index +
          1 +
          ((users?.meta?.page || 1) - 1) * (users?.meta?.perPage || perPage),
      },
      {
        title: "Learner Name",
        selector: "name",
        key: "name",

        render: ({ row }: { row: User }) => (
          <Link
            to={`/admin/cohort/learner/${row.participant.user.id}`} // Adjust the path as needed
            className="text-primary-green hover:underline"
            onClick={() =>
              sessionStorage.setItem("active-learner", JSON.stringify(row))
            }
          >
            {row?.participant?.user?.name}
          </Link>
        ),
      },
      {
        title: "Email Address",
        selector: "email",
        key: "email",
        render: ({ row }: { row: User }) => (
          <span className="">{row?.participant?.user?.email}</span>
        ),
      },
      {
        title: "Status",
        selector: null,
        key: "status",
        render: ({ row }: { row: any }) => (
          <span className="">
            {row?.status ? (
              <span
                className={`${
                  row?.status === "in progress"
                    ? "block text-primary rounded-full w-[80px] text-center py-1 border border-primary"
                    : "text-primary-green block w-[80px] text-center py-1 border border-primary-green rounded-full"
                }`}
              >
                {row?.status}
              </span>
            ) : (
              <EmptyPlaceHolder count={5} />
            )}
          </span>
        ),
      },
      {
        title: "Courses Completed",
        selector: null,
        key: "enrolledAt",
        render: ({ row }: { row: any }) => (
          <span className="text-center pl-4">
            {!row?.completedCoursesCount ? (
              <EmptyPlaceHolder count={5} />
            ) : (
              row?.completedCoursesCount
            )}
          </span>
        ),
      },
      {
        title: "Gender",
        selector: null,
        key: "userGender",
        render: ({ row }: { row: any }) => (
          <span className="text-center pl-4">
          {row?.participant?.user?.userGender[0]?.gender?.name || (
            <EmptyPlaceHolder count={5} />
          )}
        </span>
        ),
      },
      {
        title: "Country",
        selector: null,
        key: "userCountry",
        render: ({ row }: { row: any }) => (
          <span className="text-center pl-4">
            {row?.participant?.user?.userCountry[0]?.country?.name || (
              <EmptyPlaceHolder count={5} />
            )}
          </span>
        ),
      },
      {
        title: "Department",
        selector: null,
        key: "userDivision",
        render: ({ row }: { row: any }) => (
          <span className="text-center pl-4">
            {row?.participant?.user?.userDivision[0]?.division?.name || (
              <EmptyPlaceHolder count={5} />
            )}
          </span>
        ),
      },
      {
        title: "Pay Grade",
        selector: null,
        key: "userPaygrade",
        render: ({ row }: { row: any }) => (
          <span className="text-center pl-4">
            {row?.participant?.user?.userPaygrade[0]?.paygrade?.name || (
              <EmptyPlaceHolder count={5} />
            )}
          </span>
        ),
      },
      {
        title: "Date Added",
        selector: null,
        key: "createdAt",
        render: ({ row }: { row: User }) => (
          <span className="">
            {new Date(row?.participant?.createdAt).toLocaleDateString("en-ng")}
          </span>
        ),
      },
      {
        title: "Action",
        selector: null,
        key: "createdAt",
        render: ({ row }: { row: User }) => (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className="text-sm bi bi-three-dots-vertical font-semibold"
                aria-label="Customise options"
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="DropdownMenuContent"
                sideOffset={5}
              >
                <DropdownMenu.Item
                  className="DropdownMenuItem text-left  hover:bg-gray-200 text-primary-blue"
                  onClick={() => {
                    setOpenResetModal(true);
                    sessionStorage.setItem(
                      "currentLearner",
                      JSON.stringify(row)
                    );
                  }}
                >
                  Reset Password
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="DropdownMenuSeparator" />
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ),
      },
    ],
    [users?.meta?.page, users?.meta?.perPage, perPage]
  );

  const downloadData = async () => {
    toast.loading("Processing download...");

    try {
      const data = await mutateAsync({
        id: orgId,
        cohortId: activeCohortId,
        filter,
        download: true,
      });
      FileSaver.saveAs(data as any, `cohort-learners.xlsx`);
      toast.remove();
      toast.success("Downloaded successfully...");
    } catch ({ response }: any) {
      toast.remove();
      toast.error(response?.data?.message);
    }
  };

  const tableProps = { isLoading: isLoading, columns, data: users?.data || [] };

  return (
    <>
      <LearnerSummary id={id} />
      <CenteredModal
        title={`Reset Password - ${currentLearner?.participant?.user?.name}`}
        open={openResetModal}
        setOpen={setOpenResetModal}
        width={"w-[500px]"}
        btnBgColor={null}
        closeButtonLabel={null}
        onClose={null}
        withCloseBtn={false}
        hasHeader={true}
        bgColor={undefined}
        headerClassName={null}
        style={null}
      >
        {/* <ViewModules /> */}
        <ResetPassword setOpenResetModal={setOpenResetModal} />
      </CenteredModal>
      <section className="text-left pb-3 px-5 bg-white">
        <div className="flex items-center justify-between mt-2 mb-4">
          <h4 className="text-sm mt-3 font-semibold">Learners Overview</h4>
          <section className="flex items-center">
            <select
              className="input-style bg-primaryGray w-[150px] mt-4"
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by status"
            >
              <option value="">All</option>
              {filterStatus.map((item, index) => (
                <option key={index} value={item.value}>
                  {item.title}
                </option>
              ))}
            </select>
            <div className="w-[300px] ml-3 flex items-center">
              <input
                type="search"
                className="input-style bg-primaryGray w-[300px] mt-4"
                placeholder="&#128269; Search learners...."
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              onClick={downloadData}
              className="btn bg-primary-blue mt-3 pt-4  ml-3 bi bi-download"
            >
              &nbsp;&nbsp;Download Data
            </button>
          </section>
        </div>
        <section>
          <Table
            {...tableProps}
            style={{
              overflowX: "auto",
            }}
            headerClassName={"border-t bg-gray-100"}
          />
        </section>
        <section className="mt-1 px-3 rounded-lg bg-white">
          <Pagination
            data={users?.meta}
            perPage={perPage}
            pageNo={currentPage}
            setPageNo={setCurrentPage}
            setPerPage={setPerPage}
          />
        </section>
      </section>
    </>
  );
};

export default ViewLearners;
